import { useState, useMemo } from 'react';
import { useAllTenants, useDeactivateTenantMutation } from '../../../../services/hooks/useTenants';
import { Button } from '../../../../components/common/Button';
import { Tenant, TenantStatus } from '../../../../types/tenant';
import TenantFormModal from './TenantFormModal';
import {
    Search,
    Loader2,
    Users,
    UserCheck,
    UserX,
    ChevronUp,
    ChevronDown,
    Plus,
    PowerOff,
    Pencil,
    GitBranch,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

type FilterStatus = 'all' | 'active' | 'inactive';
type SortField = 'name' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const statusBadge: Record<TenantStatus, { label: string; className: string }> = {
    active: { label: 'Activo', className: 'bg-green-100 text-green-800' },
    inactive: { label: 'Inactivo', className: 'bg-red-100 text-red-800' },
    suspended: { label: 'Suspendido', className: 'bg-yellow-100 text-yellow-800' },
};

/**
 * Una fila de la tabla. `shownAsParentOnly` marca al centro que NO pasa el
 * filtro pero se pinta igual porque alguna de sus sucursales sí: es contexto
 * para que la sucursal no quede suelta, no un resultado de la búsqueda.
 */
type TenantRow = Tenant & { shownAsParentOnly?: boolean };

const buildMatcher = (searchTerm: string, filterStatus: FilterStatus) => {
    const query = searchTerm.toLowerCase();

    return (tenant: Tenant) => {
        const searchMatch =
            tenant.name.toLowerCase().includes(query) ||
            tenant.code.toLowerCase().includes(query) ||
            (tenant.cif ?? '').toLowerCase().includes(query);

        const statusMatch =
            filterStatus === 'all' ||
            (filterStatus === 'active' && tenant.status === 'active') ||
            (filterStatus === 'inactive' && tenant.status !== 'active');

        return searchMatch && statusMatch;
    };
};

const buildComparator = (sortField: SortField, sortDirection: SortDirection) => (
    (a: Tenant, b: Tenant) => {
        if (sortField === 'name') {
            return sortDirection === 'asc'
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name);
        }
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }
);

/**
 * Ordena y filtra los centros RAÍZ, y vuelve a colgar cada sucursal de su padre.
 *
 * Antes se aplanaba primero y se ordenaba después, sobre una lista que ya no
 * sabe quién es hijo de quién: con el orden por nombre, "Franco Test Sucursal"
 * —sucursal de Demo Neerutech— caía detrás de "FISIO ACTIVO CLINIC", y la
 * flecha de sangrado la hacía parecer suya. FISIO ACTIVO no tiene ninguna.
 */
const groupBranchesUnderRoot = (
    roots: Tenant[],
    matches: (tenant: Tenant) => boolean,
    compare: (a: Tenant, b: Tenant) => number,
): TenantRow[] => (
    [...roots].sort(compare).flatMap((root) => {
        const branches = (root.branches ?? [])
            .map((branch) => ({ ...branch, tenantType: 'branch' as const }))
            .filter(matches)
            .sort(compare);

        if (matches(root)) return [root, ...branches];
        // El padre no pasa el filtro pero alguna sucursal sí: se pinta igual,
        // porque dejarla suelta devuelve el mismo malentendido de antes.
        if (branches.length) return [{ ...root, shownAsParentOnly: true }, ...branches];
        return [];
    })
);

const TenantList = () => {
    const { data: response, isLoading, error, refetch } = useAllTenants();
    const deactivateMutation = useDeactivateTenantMutation();

    const [filterStatus, setFilterStatus] = useState<FilterStatus>('active');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<'name' | 'createdAt'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [showFormModal, setShowFormModal] = useState(false);
    const [tenantToEdit, setTenantToEdit] = useState<Tenant | null>(null);
    const [modalMode, setModalMode] = useState<'root' | 'branch'>('root');
    const [parentTenantForBranch, setParentTenantForBranch] = useState<Tenant | null>(null);

    const handleOpenCreate = () => {
        setTenantToEdit(null);
        setModalMode('root');
        setParentTenantForBranch(null);
        setShowFormModal(true);
    };

    const handleOpenCreateBranch = (tenant: Tenant) => {
        setTenantToEdit(null);
        setModalMode('branch');
        setParentTenantForBranch(tenant);
        setShowFormModal(true);
    };

    const handleOpenEdit = (tenant: Tenant) => {
        setTenantToEdit(tenant);
        setModalMode(tenant.tenantType === 'branch' ? 'branch' : 'root');
        setParentTenantForBranch(null);
        setShowFormModal(true);
    };

    const handleCloseModal = () => {
        setShowFormModal(false);
        setTenantToEdit(null);
        setModalMode('root');
        setParentTenantForBranch(null);
    };

    const handleSaveSuccess = () => {
        refetch();
        handleCloseModal();
    };

    const tenants: Tenant[] = response?.data ?? [];

    const flattenedTenants = useMemo(() => {
        return tenants.flatMap((tenant) => {
            const branches = tenant.branches ?? [];
            const normalizedBranches = branches.map((branch) => ({
                ...branch,
                tenantType: 'branch' as const,
            }));
            return [tenant, ...normalizedBranches];
        });
    }, [tenants]);

    const stats = useMemo(() => {
        const active = flattenedTenants.filter(t => t.status === 'active').length;
        return {
            total: flattenedTenants.length,
            active,
            inactive: flattenedTenants.length - active,
        };
    }, [flattenedTenants]);

    const filteredTenants = useMemo(() => groupBranchesUnderRoot(
        tenants,
        buildMatcher(searchTerm, filterStatus),
        buildComparator(sortField, sortDirection),
    ), [tenants, searchTerm, filterStatus, sortField, sortDirection]);

    const handleSort = (field: 'name' | 'createdAt') => {
        if (field === sortField) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleDeactivate = (tenant: Tenant) => {
        if (!confirm(`¿Desactivar el tenant "${tenant.name}"?`)) return;
        deactivateMutation.mutate(tenant._id);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                Error al cargar los tenants
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Estadísticas */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b bg-blue-50">
                <div className="flex items-center p-2 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Total Tenants</div>
                        <div className="text-xl font-bold">{stats.total}</div>
                    </div>
                </div>
                <div className="flex items-center p-2 rounded-lg bg-white shadow-sm">
                    <UserCheck className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Activos</div>
                        <div className="text-xl font-bold">{stats.active}</div>
                    </div>
                </div>
                <div className="flex items-center p-2 rounded-lg bg-white shadow-sm">
                    <UserX className="h-8 w-8 text-red-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Inactivos</div>
                        <div className="text-xl font-bold">{stats.inactive}</div>
                    </div>
                </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 p-4">
                <Button onClick={handleOpenCreate} className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Nuevo Tenant
                </Button>
            </div>

            {/* Búsqueda y filtros */}
            <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Tenants</h2>
                <div className="mt-2 flex flex-col md:flex-row gap-2">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, código o CIF"
                            className="pl-10 pr-4 py-2 border rounded w-full"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <Users size={16} />
                            <span className="hidden sm:inline">Todos</span>
                        </button>
                        <button
                            onClick={() => setFilterStatus('active')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'active' ? 'bg-green-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <UserCheck size={16} />
                            <span className="hidden sm:inline">Activos</span>
                        </button>
                        <button
                            onClick={() => setFilterStatus('inactive')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'inactive' ? 'bg-red-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <UserX size={16} />
                            <span className="hidden sm:inline">Inactivos</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Contador */}
            <div className="px-4 py-2 bg-white border-b text-sm text-gray-500">
                <strong>Mostrando {filteredTenants.length}</strong> de {stats.total} tenants
            </div>

            {/* Tabla */}
            {filteredTenants.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    No se encontraron tenants con los filtros aplicados
                </div>
            ) : (
                <div className="w-full overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSort('name')}
                                >
                                    <div className="flex items-center gap-1">
                                        Nombre
                                        {sortField === 'name' && (
                                            sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    CIF
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Código
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Salas
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSort('createdAt')}
                                >
                                    <div className="flex items-center gap-1">
                                        Creado
                                        {sortField === 'createdAt' && (
                                            sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTenants.map(tenant => {
                                const badge = statusBadge[tenant.status] ?? statusBadge.inactive;
                                return (
                                    <tr
                                        key={tenant._id}
                                        className={tenant.shownAsParentOnly ? 'bg-gray-50/60' : 'hover:bg-gray-50'}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className={`text-sm font-medium ${tenant.shownAsParentOnly ? 'text-gray-400' : 'text-gray-900'}`}>
                                                {tenant.tenantType === 'branch' && <span className="mr-2 text-gray-400">↳</span>}
                                                {tenant.name}
                                            </div>
                                            {tenant.shownAsParentOnly && (
                                                <div className="text-xs text-gray-400">
                                                    No coincide con el filtro; sale por sus sucursales
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tenant.tenantType === 'branch' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'}`}>
                                                {tenant.tenantType === 'branch' ? 'Sucursal' : 'Raíz'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="font-mono text-xs text-gray-600">{tenant.cif ?? '-'}</span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-gray-700">
                                                {tenant.code}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${badge.className}`}>
                                                {badge.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {tenant.limits.roomsQty}
                                            {tenant.limits.roomCapacityDefault !== null && (
                                                <span className="text-xs text-gray-400 ml-1">
                                                    (cap. {tenant.limits.roomCapacityDefault})
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {format(new Date(tenant.createdAt), 'dd MMM yyyy', { locale: es })}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleOpenEdit(tenant)}
                                                    className="text-gray-500 hover:text-blue-600 flex items-center gap-1 text-sm"
                                                    title="Editar"
                                                >
                                                    <Pencil size={15} />
                                                    Editar
                                                </button>
                                                {tenant.tenantType === 'root' && (
                                                    <button
                                                        onClick={() => handleOpenCreateBranch(tenant)}
                                                        className="text-indigo-500 hover:text-indigo-700 flex items-center gap-1 text-sm"
                                                        title="Añadir sucursal"
                                                    >
                                                        <GitBranch size={15} />
                                                        Sucursal
                                                    </button>
                                                )}
                                                {tenant.status === 'active' && (
                                                    <button
                                                        onClick={() => handleDeactivate(tenant)}
                                                        disabled={deactivateMutation.isPending}
                                                        className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm disabled:opacity-50"
                                                        title="Desactivar"
                                                    >
                                                        <PowerOff size={15} />
                                                        Desactivar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <TenantFormModal
                isOpen={showFormModal}
                onClose={handleCloseModal}
                onSaveSuccess={handleSaveSuccess}
                tenantToEdit={tenantToEdit}
                mode={modalMode}
                parentTenant={parentTenantForBranch}
            />
        </div>
    );
};

export default TenantList;
